// pages/MerchantOnboarding.jsx
//
// Account Entry (Screen 1) for the progressive-onboarding model.
// See docs/onboarding-analysis/ONBOARDING_UX_REDESIGN.md §2 & §8.
//
// Goal: minimum dataset, < 60s. Collect only name, brand, username, mobile (no OTP),
// email and password here — everything else (address, KYC docs, GST, bank, wallet) is
// deferred to the Onboarding Hub. On success we create the account, auto-login and land
// the merchant on the Hub (/onboarding), not an empty shipments table.
//
// Phase A (this file): live username-availability check + full inline validation.
// Phase B (later): email OTP (send/verify) will gate the primary CTA — the email field
// is intentionally structured so that step can slot in without a redesign.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch } from 'react-redux';
import { jwtDecode } from 'jwt-decode';

import { restoreSession } from '../redux/slices/authSlice';
import {
  registerUser,
  authenticateUser,
  checkUsernameAvailable,
  initiateOnboarding,
  updateOnboardingDetails,
} from '../services/onboardingService';
import { saveMerchantSignupToSheet } from '../services/googleSheetsService';
import { clearClientCache } from '../utils/clearClientCache';

// ── Validation helpers ──────────────────────────────────────────────────────
const USERNAME_REGEX = /^[a-zA-Z0-9._]{4,30}$/;
const EMAIL_REGEX = /^\S+@\S+\.\S+$/;
const MOBILE_REGEX = /^[6-9]\d{9}$/;

function scorePassword(pw) {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return Math.min(score, 4);
}

const STRENGTH = [
  { label: 'Too weak', color: 'bg-red-500', text: 'text-red-600' },
  { label: 'Weak', color: 'bg-orange-500', text: 'text-orange-600' },
  { label: 'Fair', color: 'bg-amber-500', text: 'text-amber-600' },
  { label: 'Good', color: 'bg-lime-500', text: 'text-lime-600' },
  { label: 'Strong', color: 'bg-green-600', text: 'text-green-700' },
];

// ── Small inline icons (no icon-lib dependency) ─────────────────────────────
const Spinner = ({ className = 'w-4 h-4' }) => (
  <svg className={`${className} animate-spin`} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
  </svg>
);
const CheckIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd" /></svg>
);
const XIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.3 4.3a1 1 0 011.4 0L10 8.6l4.3-4.3a1 1 0 111.4 1.4L11.4 10l4.3 4.3a1 1 0 01-1.4 1.4L10 11.4l-4.3 4.3a1 1 0 01-1.4-1.4L8.6 10 4.3 5.7a1 1 0 010-1.4z" clipRule="evenodd" /></svg>
);

// ── Reusable field ──────────────────────────────────────────────────────────
function Field({ label, error, touched, hint, hintClassName = 'text-gray-400', children, htmlFor }) {
  const show = touched && error;
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      {children}
      <div className="min-h-[18px] mt-1">
        {show ? (
          <p className="text-xs text-red-600">{error}</p>
        ) : hint ? (
          <p className={`text-xs ${hintClassName}`}>{hint}</p>
        ) : null}
      </div>
    </div>
  );
}

const inputBase =
  'w-full px-3.5 py-2.5 rounded-xl border bg-white text-gray-900 placeholder-gray-400 outline-none transition-all duration-200 focus:ring-2';

function inputCls(invalid) {
  return `${inputBase} ${invalid
    ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
    : 'border-gray-200 focus:border-blue-400 focus:ring-blue-100'}`;
}

const ValueProp = ({ title, desc }) => (
  <div className="flex items-start gap-3">
    <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white/15 text-white">
      <CheckIcon className="w-4 h-4" />
    </div>
    <div>
      <p className="font-semibold text-white">{title}</p>
      <p className="text-sm text-white/70">{desc}</p>
    </div>
  </div>
);

// Gently-floating glass chip (decorative) built from an existing brand SVG.
const FloatingChip = ({ src, className = '', delay = 0 }) => (
  <motion.div
    aria-hidden
    className={`pointer-events-none absolute z-0 hidden h-14 w-14 items-center justify-center rounded-2xl border border-white/20 bg-white/10 backdrop-blur xl:flex ${className}`}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1, y: [0, -10, 0] }}
    transition={{ opacity: { duration: 0.6, delay }, y: { duration: 6, repeat: Infinity, ease: 'easeInOut', delay } }}
  >
    <img src={src} alt="" className="h-7 w-7 opacity-90" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
  </motion.div>
);

const VALUE_PROPS = [
  { title: 'No paperwork upfront', desc: 'Just the basics to get started. Add KYC & bank details only before you ship.' },
  { title: 'One dashboard', desc: 'Orders, tracking, NDR, COD remittance and reports in a single place.' },
  { title: 'Best courier rates', desc: 'Auto-allocation across carriers for the best price and serviceability.' },
];

const STATS = [
  { n: '1,000+', l: 'Merchants' },
  { n: '25+', l: 'Couriers' },
  { n: '99.9%', l: 'Uptime' },
];

const MerchantOnboarding = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [form, setForm] = useState({
    firstName: '', lastName: '', brandName: '',
    username: '', mobile: '', email: '',
    password: '', confirmPassword: '',
  });
  const [touched, setTouched] = useState({});
  const [agree, setAgree] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // username availability: idle | invalid | checking | available | taken | unknown
  const [usernameState, setUsernameState] = useState({ status: 'idle', message: '' });
  const usernameSeq = useRef(0);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const blur = (key) => () => setTouched((t) => ({ ...t, [key]: true }));

  const errors = useMemo(() => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'First name is required';
    if (!form.lastName.trim()) e.lastName = 'Last name is required';
    if (!form.brandName.trim()) e.brandName = 'Brand name is required';
    if (!form.username.trim()) e.username = 'Choose a username';
    else if (!USERNAME_REGEX.test(form.username.trim())) e.username = '4–30 chars: letters, numbers, dot or underscore';
    if (!form.mobile.trim()) e.mobile = 'Mobile number is required';
    else if (!MOBILE_REGEX.test(form.mobile.trim())) e.mobile = 'Enter a valid 10-digit mobile number';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!EMAIL_REGEX.test(form.email.trim())) e.email = 'Enter a valid email address';
    if (!form.password) e.password = 'Create a password';
    else if (form.password.length < 8) e.password = 'Use at least 8 characters';
    if (!form.confirmPassword) e.confirmPassword = 'Re-enter your password';
    else if (form.confirmPassword !== form.password) e.confirmPassword = 'Passwords do not match';
    return e;
  }, [form]);

  const pwScore = scorePassword(form.password);

  // Debounced username availability check.
  useEffect(() => {
    const u = form.username.trim();
    if (!u) { setUsernameState({ status: 'idle', message: '' }); return; }
    if (!USERNAME_REGEX.test(u)) { setUsernameState({ status: 'invalid', message: '' }); return; }

    setUsernameState({ status: 'checking', message: 'Checking availability…' });
    const seq = ++usernameSeq.current;
    const t = setTimeout(async () => {
      const { available } = await checkUsernameAvailable(u);
      if (seq !== usernameSeq.current) return; // stale
      if (available === true) setUsernameState({ status: 'available', message: 'Username is available' });
      else if (available === false) setUsernameState({ status: 'taken', message: 'That username is already taken' });
      else setUsernameState({ status: 'unknown', message: "Couldn't verify right now — you can still continue" });
    }, 500);
    return () => clearTimeout(t);
  }, [form.username]);

  const canSubmit =
    Object.keys(errors).length === 0 &&
    agree &&
    usernameState.status !== 'taken' &&
    usernameState.status !== 'checking' &&
    !submitting;

  const handleSubmit = useCallback(async (evt) => {
    evt?.preventDefault?.();
    setTouched({ firstName: true, lastName: true, brandName: true, username: true, mobile: true, email: true, password: true, confirmPassword: true });
    if (Object.keys(errors).length > 0 || !agree) return;
    if (usernameState.status === 'taken' || usernameState.status === 'checking') return;

    setSubmitting(true);
    setError(null);
    try {
      const brand = form.brandName.trim();
      const reg = await registerUser({
        organization_name: brand,
        brand_name: brand,
        platform_id: 1,
        is_standard_user: true,
        username: form.username.trim(),
        password: form.password,
        email: form.email.trim(),
        role_id: 1,
        firstname: form.firstName.trim(),
        lastname: form.lastName.trim(),
      });
      if (!reg?.success) {
        throw new Error(reg?.error || 'We could not create your account. Please try again.');
      }

      let token = await authenticateUser({ username: form.username.trim(), password: form.password });
      let decoded = jwtDecode(token);
      const clientId = decoded.clientId || decoded.parentClientId;
      let merchantId = decoded.merchantId;

      // Create the POCAS onboarding record if the token doesn't already carry a merchantId.
      // Best-effort: never block account creation on a downstream hiccup.
      if (!merchantId) {
        try {
          const initiated = await initiateOnboarding(clientId, token);
          merchantId = initiated?.merchant_id || initiated?.merchantId;
          const fresh = await authenticateUser({ username: form.username.trim(), password: form.password });
          token = fresh;
          decoded = jwtDecode(fresh);
          merchantId = decoded.merchantId || merchantId;
        } catch (initErr) {
          console.warn('initiate onboarding failed (continuing):', initErr?.message);
          merchantId = merchantId || clientId;
        }
      }

      // Start the new account on a clean slate — clear any cached data left by a previous session
      // on this browser before persisting the new one (branding is preserved).
      clearClientCache();

      // Persist session for the rest of the app (redux + localStorage fallbacks used by the hub/guard).
      try {
        localStorage.setItem('authToken', token);
        if (clientId) localStorage.setItem('parentClientId', clientId);
        if (merchantId) localStorage.setItem('merchantId', merchantId);
      } catch (_) { /* storage may be unavailable */ }
      dispatch(restoreSession({ token, user: decoded }));

      // Seed the minimal ACCOUNT-phase details so the Hub shows progress. Best-effort only.
      try {
        await updateOnboardingDetails({
          parent_client_id: clientId,
          merchant_id: merchantId,
          basic_details: {
            email: form.email.trim(),
            phone: form.mobile.trim(),
            name: `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
            brand_name: brand,
            email_verified: false,
            phone_verified: false,
          },
          // Required by POCAS on update (drives wallet creation). Default to PREPAID at signup;
          // the merchant can change wallet preference later during progressive onboarding.
          wallet_type_preference: 'PREPAID',
        }, token);
      } catch (updErr) {
        console.warn('seeding basic details failed (continuing):', updErr?.message);
      }

      // Non-critical signup tracking.
      try {
        await saveMerchantSignupToSheet({
          merchantId, username: form.username.trim(), password: form.password,
          parentClientId: clientId, email: form.email.trim(), phone: form.mobile.trim(),
          name: `${form.firstName.trim()} ${form.lastName.trim()}`.trim(), brandName: brand,
        });
      } catch (_) { /* optional */ }

      // Land the merchant in the guided onboarding wizard with a celebratory intro. All the
      // account-creation API calls above are unchanged; the wizard picks up progressively from here.
      navigate('/onboarding', { replace: true, state: { justSignedUp: true } });
    } catch (err) {
      console.error('Signup failed:', err);
      setError(err?.message || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  }, [errors, agree, usernameState.status, form, dispatch, navigate]);

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="mx-auto grid min-h-screen max-w-6xl grid-cols-1 lg:grid-cols-2">
        {/* Left — brand / value panel */}
        <div className="relative hidden overflow-hidden bg-gradient-to-br from-indigo-700 via-blue-700 to-violet-800 p-10 lg:flex lg:flex-col lg:justify-between">
          <motion.div
            aria-hidden
            className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-white/10 blur-3xl"
            animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            aria-hidden
            className="pointer-events-none absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-fuchsia-400/20 blur-3xl"
            animate={{ scale: [1.1, 1, 1.1] }} transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            aria-hidden
            className="pointer-events-none absolute right-1/3 top-1/2 h-56 w-56 rounded-full bg-cyan-300/15 blur-3xl"
            animate={{ scale: [1, 1.2, 1], x: [0, 24, 0] }} transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
          />
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.07]"
            style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg,#fff 1px, transparent 1px)', backgroundSize: '42px 42px' }}
            aria-hidden
          />
          <FloatingChip src="/bulk_shipments.svg" className="left-10 top-44" delay={0} />
          <FloatingChip src="/reports.svg" className="right-12 top-28" delay={0.6} />
          <FloatingChip src="/wallet_wayuvega.svg" className="bottom-44 right-16" delay={1.2} />

          <div className="relative z-10 flex items-center gap-2">
            <div className="text-2xl font-extrabold tracking-tight text-white">Wayuvega</div>
            <span className="rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-semibold text-white/90">for business</span>
          </div>

          <div className="relative z-10 space-y-8">
            <div>
              <motion.h1
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                className="text-[42px] font-extrabold leading-[1.1] text-white"
              >
                Ship smarter.<br />Grow faster.
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
                className="mt-3 max-w-md text-lg text-white/80"
              >
                Create your account now and finish KYC later — explore the dashboard right away.
              </motion.p>
            </div>

            <div className="space-y-4">
              {VALUE_PROPS.map((vp, i) => (
                <motion.div key={vp.title} initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.1, duration: 0.4 }}>
                  <ValueProp title={vp.title} desc={vp.desc} />
                </motion.div>
              ))}
            </div>

            <div className="grid max-w-md grid-cols-3 gap-3">
              {STATS.map((s, i) => (
                <motion.div
                  key={s.l}
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 + i * 0.1 }}
                  className="rounded-2xl border border-white/15 bg-white/10 px-3 py-3 backdrop-blur"
                >
                  <div className="text-xl font-bold text-white">{s.n}</div>
                  <div className="text-xs text-white/70">{s.l}</div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }}
              className="max-w-md rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur"
            >
              <p className="text-sm leading-relaxed text-white/90">“We onboarded in minutes and shipped the same day — the dashboard is a breeze.”</p>
              <div className="mt-3 flex items-center gap-2.5">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-white/25 text-xs font-bold text-white">A</div>
                <div className="text-xs text-white/70">Ops Lead · growing D2C brand</div>
              </div>
            </motion.div>
          </div>

          <div className="relative z-10 flex items-center gap-2 text-sm text-white/60">
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001z" clipRule="evenodd" /></svg>
            Bank-grade security · Trusted by growing D2C brands across India.
          </div>
        </div>

        {/* Right — form */}
        <div className="flex items-center justify-center p-5 sm:p-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="w-full max-w-md"
          >
            <div className="mb-6 flex items-center justify-between">
              <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                Step 1 of 6 · under a minute
              </span>
              <span className="text-sm text-gray-500">
                Already have an account?{' '}
                <Link to="/login" className="font-semibold text-blue-600 hover:underline">Log in</Link>
              </span>
            </div>

            <h2 className="text-2xl font-bold text-gray-900">Create your account</h2>
            <p className="mt-1 text-sm text-gray-500">Finish the rest later — you can explore the app right away.</p>

            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                  className="mt-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-700"
                >
                  <XIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </motion.div>
              )}
            </AnimatePresence>

            <form className="mt-5" onSubmit={handleSubmit} noValidate>
              <div className="grid grid-cols-2 gap-x-4">
                <Field label="First name" htmlFor="firstName" error={errors.firstName} touched={touched.firstName}>
                  <input id="firstName" type="text" autoComplete="given-name" value={form.firstName}
                    onChange={set('firstName')} onBlur={blur('firstName')} className={inputCls(touched.firstName && errors.firstName)} placeholder="Aditi" />
                </Field>
                <Field label="Last name" htmlFor="lastName" error={errors.lastName} touched={touched.lastName}>
                  <input id="lastName" type="text" autoComplete="family-name" value={form.lastName}
                    onChange={set('lastName')} onBlur={blur('lastName')} className={inputCls(touched.lastName && errors.lastName)} placeholder="Sharma" />
                </Field>
              </div>

              <Field label="Brand / business name" htmlFor="brandName" error={errors.brandName} touched={touched.brandName}>
                <input id="brandName" type="text" autoComplete="organization" value={form.brandName}
                  onChange={set('brandName')} onBlur={blur('brandName')} className={inputCls(touched.brandName && errors.brandName)} placeholder="Acme Retail" />
              </Field>

              {/* Username with live availability */}
              <Field
                label="Username" htmlFor="username"
                error={errors.username} touched={touched.username}
                hintClassName={
                  usernameState.status === 'available' ? 'text-green-600'
                  : usernameState.status === 'taken' ? 'text-red-600'
                  : usernameState.status === 'unknown' ? 'text-amber-600'
                  : 'text-gray-400'
                }
                hint={
                  usernameState.status === 'checking' ? 'Checking availability…'
                  : usernameState.status === 'available' ? 'Username is available'
                  : usernameState.status === 'taken' ? 'That username is already taken'
                  : usernameState.status === 'unknown' ? "Couldn't verify right now — you can still continue"
                  : 'You’ll use this to log in'
                }
              >
                <div className="relative">
                  <input
                    id="username" type="text" autoComplete="username" value={form.username}
                    onChange={set('username')} onBlur={blur('username')}
                    className={`${inputCls((touched.username && errors.username) || usernameState.status === 'taken')} pr-10`}
                    placeholder="acme_retail"
                  />
                  <div className="absolute inset-y-0 right-3 flex items-center">
                    {usernameState.status === 'checking' && <Spinner className="h-4 w-4 text-gray-400" />}
                    {usernameState.status === 'available' && <CheckIcon className="h-5 w-5 text-green-600" />}
                    {usernameState.status === 'taken' && <XIcon className="h-5 w-5 text-red-500" />}
                  </div>
                </div>
              </Field>

              <div className="grid grid-cols-2 gap-x-4">
                <Field label="Mobile" htmlFor="mobile" error={errors.mobile} touched={touched.mobile} hint="For shipment updates">
                  <input id="mobile" type="tel" inputMode="numeric" maxLength={10} autoComplete="tel" value={form.mobile}
                    onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value.replace(/\D/g, '') }))} onBlur={blur('mobile')}
                    className={inputCls(touched.mobile && errors.mobile)} placeholder="9876543210" />
                </Field>
                <Field label="Email" htmlFor="email" error={errors.email} touched={touched.email} hint="We’ll send updates here">
                  <input id="email" type="email" autoComplete="email" value={form.email}
                    onChange={set('email')} onBlur={blur('email')} className={inputCls(touched.email && errors.email)} placeholder="you@brand.com" />
                </Field>
              </div>

              {/* Password */}
              <Field label="Password" htmlFor="password" error={errors.password} touched={touched.password}>
                <div className="relative">
                  <input id="password" type={showPw ? 'text' : 'password'} autoComplete="new-password" value={form.password}
                    onChange={set('password')} onBlur={blur('password')} className={`${inputCls(touched.password && errors.password)} pr-16`} placeholder="At least 8 characters" />
                  <button type="button" onClick={() => setShowPw((s) => !s)}
                    className="absolute inset-y-0 right-3 my-auto text-xs font-semibold text-gray-500 hover:text-gray-700">
                    {showPw ? 'Hide' : 'Show'}
                  </button>
                </div>
                {form.password && (
                  <div className="mt-2">
                    <div className="flex gap-1">
                      {[0, 1, 2, 3].map((i) => (
                        <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i < pwScore ? STRENGTH[pwScore].color : 'bg-gray-200'}`} />
                      ))}
                    </div>
                    <p className={`mt-1 text-xs font-medium ${STRENGTH[pwScore].text}`}>{STRENGTH[pwScore].label}</p>
                  </div>
                )}
              </Field>

              <Field label="Confirm password" htmlFor="confirmPassword" error={errors.confirmPassword} touched={touched.confirmPassword}>
                <input id="confirmPassword" type={showPw ? 'text' : 'password'} autoComplete="new-password" value={form.confirmPassword}
                  onChange={set('confirmPassword')} onBlur={blur('confirmPassword')} className={inputCls(touched.confirmPassword && errors.confirmPassword)} placeholder="Re-enter password" />
              </Field>

              <label className="mt-1 flex cursor-pointer items-start gap-2.5 text-sm text-gray-600">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span>
                  I agree to the{' '}
                  <Link to="/terms-and-conditions" className="font-medium text-blue-600 hover:underline">Terms</Link> and{' '}
                  <Link to="/privacy-policy" className="font-medium text-blue-600 hover:underline">Privacy Policy</Link>.
                </span>
              </label>

              <motion.button
                type="submit" disabled={!canSubmit}
                whileHover={canSubmit ? { scale: 1.01 } : undefined}
                whileTap={canSubmit ? { scale: 0.99 } : undefined}
                className={`relative mt-5 flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl px-5 py-3.5 text-sm font-semibold text-white shadow-lg transition-all ${
                  canSubmit ? 'bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 hover:shadow-blue-500/30' : 'cursor-not-allowed bg-gray-300'
                }`}
              >
                {canSubmit && (
                  <motion.span
                    aria-hidden
                    className="absolute inset-y-0 -left-1/3 w-1/3 -skew-x-12 bg-white/25 blur-md"
                    animate={{ x: ['0%', '420%'] }}
                    transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut', repeatDelay: 1.1 }}
                  />
                )}
                <span className="relative flex items-center gap-2">
                  {submitting ? (<><Spinner className="h-4 w-4" /> Creating your account…</>) : (<>Create account &amp; continue →</>)}
                </span>
              </motion.button>

              <p className="mt-4 text-center text-xs text-gray-400">
                You can explore the app right away. KYC &amp; bank details are needed only before you ship.
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default MerchantOnboarding;
